import { useState, useCallback, useRef, useEffect } from "react";
import { ReactNode } from "react";
import {
  createAlertsFromDetection,
  createGenericAlert,
} from "../../utils/alertTemplates";
import {
  isDelegationFunctionCallName,
  parseDelegationOpportunityFromFunctionCall,
} from "types/delegation";

const WEBSOCKET_TIMEOUT_MS = 60000;
const API_REFRESH_INTERVAL_MS = 10000;

// Message source types from backend data model
type MessageSource = "system" | "human" | "agent" | "";

/**
 * Helper function to process events and attach added event information
 * to calendar events for proper tracking.
 */
export function processEventsWithAddedData(
  events: string[],
  addedEventsMap: Map<
    number,
    {
      eventRef: string;
      eventStatus: string;
      eventModified: boolean;
      sourceEntityType: string;
      sourceEntityId: string;
      sourceEntityIndex: number;
    }
  >,
): string[] {
  let globalEventIndex = 0;
  return events.map((eventString: string) => {
    const event = JSON.parse(eventString);

    if (event.eventType === "function_call") {
      const functionCallData = JSON.parse(event.content);
      if (functionCallData?.name === "add_calendar_events") {
        const eventCount = functionCallData?.args?.events?.length || 0;
        const groupAddedEvents: Array<{
          index: number;
          eventRef: string;
          eventStatus: string;
          eventModified: boolean;
        }> = [];

        for (let i = 0; i < eventCount; i++) {
          const addedEvent = addedEventsMap.get(globalEventIndex);
          if (addedEvent) {
            groupAddedEvents.push({
              index: i,
              eventRef: addedEvent.eventRef,
              eventStatus: addedEvent.eventStatus,
              eventModified: addedEvent.eventModified,
            });
          }
          globalEventIndex++;
        }

        functionCallData.addedEvents = groupAddedEvents;
        event.content = JSON.stringify(functionCallData);
      }
    }

    return JSON.stringify(event);
  });
}

/**
 * Determines if a message should be displayed in the chat UI
 */
function determineMessageDisplay(source: MessageSource): boolean {
  if (source === "system") return false;
  if (source === "human") return true;
  if (source === "agent") return false;
  // If no source, assume it's a system message ( we addded source later )
  if (!source) return false;
  return false;
}

export interface WebSource {
  uri: string;
  title: string;
}
export interface ForumSource {
  post_ref: string;
  author_ref: string;
  author_name: string;
  title: string;
  text_preview: string;
  relevance_explanation: string;
}

export interface BrowserUseSource {
  step_number: number;
  description: string;
  url: string;
  screenshot_url: string;
}

export interface FunctionCallAttachment {
  url: string;
  content_type: string;
  filename: string;
}

export interface FunctionCall {
  id: string;
  name: string;
  displayName: string;
  args: Record<string, any>;
  state: "queued" | "running" | "completed";
  result?: string;
  executionTimeSec?: number;
  startTime?: number;
  endTime?: number;
  sources?: WebSource[] | ForumSource[] | BrowserUseSource[];
  attachments?: FunctionCallAttachment[];
  planId?: string;
}

export interface PlanDetail {
  title: string;
  value?: string;
  isExcluded?: boolean;
  ref?: string;
  refType?: string;
}

export interface TodoItem {
  id?: string;
  content?: string;
  step?: string;
  status: "pending" | "in_progress" | "completed";
  priority?: "high" | "medium" | "low";
  details?: PlanDetail[];
}

export interface BookmarkItem {
  id: string;
  type:
    | "task"
    | "person"
    | "document"
    | "location"
    | "resource"
    | "idea"
    | "issue";
  title: string;
  reason: string;
}

export interface UserQuestion {
  id: string;
  question: string;
  timestamp: number;
  answered?: boolean;
  question_type?:
    | "string"
    | "number"
    | "date"
    | "datetime"
    | "range"
    | "single_select"
    | "multi_select";
  options?: string[];
  answer?: string;
}

export interface AlertItem {
  id: string;
  title: string;
  description?: string | ReactNode;
  level: "info" | "warning" | "error" | "success";
  dismissable?: boolean;
  timestamp: number;
  dismissed?: boolean;
}

export interface CalendarEvent {
  title: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  description?: string;
  timezone: string;
  location?: string;
}

export interface AddedEventInfo {
  eventRef: string;
  eventStatus: string;
  eventModified: boolean;
}

export interface CalendarEventGroup {
  id: string;
  calendar_name: string;
  events: CalendarEvent[];
  timestamp: number;
  addedEventIndices?: Set<number>;
  addedEvents?: Map<number, AddedEventInfo>;
}

export interface AddedTask {
  taskRef: string;
  taskTitle: string;
  taskState: string;
  taskPriority: string;
  taskStage: string;
  taskDueDate?: string;
  taskProject?: string;
  taskStageDate?: string;
}

export interface AddedDocument {
  ref: string;
  uuid: string;
  title: string;
  subtitle: string;
  documentType: number;
  scope: string;
  ownerRef: string;
  ownerDisplayName: string;
  lastUpdatedSec: number;
  unreadCount: number;
}

export type DelegationStatus =
  | "pending"
  | "queued"
  | "in_progress"
  | "delegated"
  | "completed"
  | "cancelled";

export interface DelegationOpportunity {
  functionId: string;
  ref?: string;
  title: string;
  description: string;
  details: Array<{
    name: string;
    value: string;
    detailType?: string;
    entityType?: string;
  }>;
  status: DelegationStatus;
  delegated_at?: number;
  completed_at?: number;
  serviceType: string;
  agentThreadRef?: string;
  time_spent_minutes?: number;
  actionDateSec?: number;
  timeEstimateMinutes?: number;
}

export interface DelegationOpportunityGroup {
  id: string;
  opportunities: DelegationOpportunity[];
  timestamp: number;
}

export interface ChatMessage {
  content: string;
  source: "system" | "user" | "agent";
  contentHtml?: string;
  outputCallIds?: string[];
}

export interface TaskAgentState {
  chatMessages: ChatMessage[];
  thinkingMessage: string;
  functionCalls: FunctionCall[];
  todos: TodoItem[];
  bookmarks: BookmarkItem[];
  userQuestions: UserQuestion[];
  calendarEvents: CalendarEventGroup[];
  addedTasks: AddedTask[];
  addedDocuments: AddedDocument[];
  delegationOpportunities: DelegationOpportunityGroup[];
  alerts: AlertItem[];
  isActive: boolean;
  lastEventTime: number;
  hasFailed: boolean;
  hasCompleted: boolean;
  currentPlanId?: string;
  _isNewChatMessage?: boolean;
  _hadUserMessageSinceModelStart?: boolean;
  _pendingOutputCallIds?: string[];
  clientOutputVisible: boolean;
  playbookRef?: string;
}

export interface BackendDelegationTask {
  ref: string;
  functionCallId: string;
  title: string;
  description: string;
  detailsJson: string;
  status: string;
  delegatedSec: bigint;
  completedSec: bigint;
  timeSpentMinutes: number;
  serviceType: string;
}

export interface TaskAgentStateManager {
  state: TaskAgentState;
  actions: {
    handleWebSocketMessage: (channel: string, data: any) => void;
    resetState: () => void;
    updateState: (updates: Partial<TaskAgentState>) => void;
    dismissAlert: (alertId: string) => void;
    mergeDelegationTasks: (tasks: BackendDelegationTask[]) => void;
  };
  computed: {
    isCurrentlyActive: boolean;
    generalFunctionCalls: FunctionCall[];
    visibleAlerts: AlertItem[];
    planWithActions: Array<{
      plan: TodoItem;
      actions: FunctionCall[];
    }>;
    unassociatedActions: FunctionCall[];
    hasContent: boolean;
  };
}

export interface UseTaskAgentStateOptions {
  staticState?: Partial<TaskAgentState>;
  onRefresh?: () => Promise<void>;
  websocketTimeoutMs?: number;
  apiRefreshIntervalMs?: number;
  accountType?: "advisor" | "member";
}

const initialState: TaskAgentState = {
  chatMessages: [],
  thinkingMessage: "",
  functionCalls: [],
  todos: [],
  bookmarks: [],
  userQuestions: [],
  calendarEvents: [],
  addedTasks: [],
  addedDocuments: [],
  delegationOpportunities: [],
  alerts: [],
  isActive: false,
  lastEventTime: 0,
  hasFailed: false,
  hasCompleted: false,
  currentPlanId: undefined,
  clientOutputVisible: false,
  playbookRef: undefined,
};

export const useTaskAgentState = (
  options?: UseTaskAgentStateOptions,
): TaskAgentStateManager => {
  const {
    staticState,
    onRefresh,
    websocketTimeoutMs = WEBSOCKET_TIMEOUT_MS,
    apiRefreshIntervalMs = API_REFRESH_INTERVAL_MS,
    accountType = "advisor",
  } = options || {};

  const [state, setState] = useState<TaskAgentState>({
    ...initialState,
    ...staticState,
  });

  const lastWebsocketEventTimeRef = useRef(0);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateState = useCallback((updates: Partial<TaskAgentState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  const dismissAlert = useCallback((alertId: string) => {
    setState((prev) => ({
      ...prev,
      alerts: prev.alerts.map((alert) =>
        alert.id === alertId ? { ...alert, dismissed: true } : alert,
      ),
    }));
  }, []);

  const mergeDelegationTasks = useCallback((tasks: BackendDelegationTask[]) => {
    if (!tasks || tasks.length === 0) return;

    setState((prev) => {
      const tasksByFunctionId = new Map(
        tasks.map((t) => [t.functionCallId, t]),
      );

      let updatedGroups = prev.delegationOpportunities.map((group) => ({
        ...group,
        opportunities: group.opportunities.map((opp) => {
          const backendTask = tasksByFunctionId.get(opp.functionId);
          if (backendTask) {
            tasksByFunctionId.delete(opp.functionId);
            let details = opp.details;
            try {
              if (backendTask.detailsJson) {
                details = JSON.parse(backendTask.detailsJson);
              }
            } catch {
              // Keep original details if parsing fails
            }
            return {
              ...opp,
              ref: backendTask.ref,
              title: backendTask.title || opp.title,
              description: backendTask.description || opp.description,
              details,
              status: (backendTask.status as DelegationStatus) || opp.status,
              serviceType: backendTask.serviceType || opp.serviceType,
              delegated_at: backendTask.delegatedSec
                ? Number(backendTask.delegatedSec) * 1000
                : opp.delegated_at,
              completed_at: backendTask.completedSec
                ? Number(backendTask.completedSec) * 1000
                : opp.completed_at,
              time_spent_minutes:
                backendTask.timeSpentMinutes || opp.time_spent_minutes,
            };
          }
          return opp;
        }),
      }));

      // Add any backend tasks that weren't matched to existing opportunities
      if (tasksByFunctionId.size > 0) {
        const newOpportunities: DelegationOpportunity[] = [];
        tasksByFunctionId.forEach((task) => {
          let details: Array<{ name: string; value: string }> = [];
          try {
            if (task.detailsJson) {
              details = JSON.parse(task.detailsJson);
            }
          } catch {
            // Keep empty details if parsing fails
          }
          newOpportunities.push({
            functionId: task.functionCallId,
            ref: task.ref,
            title: task.title,
            description: task.description,
            details,
            status: (task.status as DelegationStatus) || "pending",
            serviceType: task.serviceType,
            delegated_at: task.delegatedSec
              ? Number(task.delegatedSec) * 1000
              : undefined,
            completed_at: task.completedSec
              ? Number(task.completedSec) * 1000
              : undefined,
            time_spent_minutes: task.timeSpentMinutes,
          });
        });

        if (newOpportunities.length > 0) {
          const existingGroup = updatedGroups.find(
            (g) => g.id === "default-group",
          );
          if (existingGroup) {
            updatedGroups = updatedGroups.map((g) =>
              g.id === "default-group"
                ? {
                    ...g,
                    opportunities: [...g.opportunities, ...newOpportunities],
                  }
                : g,
            );
          } else {
            updatedGroups.push({
              id: "default-group",
              opportunities: newOpportunities,
              timestamp: Date.now(),
            });
          }
        }
      }

      return {
        ...prev,
        delegationOpportunities: updatedGroups,
      };
    });
  }, []);

  const handleWebSocketMessage = useCallback((channel: string, data: any) => {
    console.debug("Received message:", channel, data);
    const eventTime = Date.now();

    lastWebsocketEventTimeRef.current = eventTime;

    setState((prev) => ({ ...prev, lastEventTime: eventTime }));

    // Check for user activity based on role/source fields (handles user updates, chats, etc.)
    // This ensures _hadUserMessageSinceModelStart is set even for event types we don't explicitly handle
    const isUserActivity =
      data.role === "user" &&
      (data.source === "human" ||
        data.type === "update" ||
        data.type === "chat");
    if (isUserActivity) {
      setState((prev) => ({
        ...prev,
        _hadUserMessageSinceModelStart: true,
      }));
    }

    if (!data.eventType) {
      // Handle history format events that use 'type' instead of 'eventType'
      if (data.role === "user") {
        // User chat messages should be added to chatMessages (for proper grouping)
        // User update messages just set the flag (already done above)
        if (
          data.type === "chat" &&
          data.source === "human" &&
          data.content?.content
        ) {
          const content = data.content.content;
          setState((prev) => ({
            ...prev,
            chatMessages: [...prev.chatMessages, { content, source: "user" }],
          }));
        }
        return;
      }
      if (
        data.role === "model" &&
        data.type === "chat" &&
        data.content?.content
      ) {
        // Model chat messages in history format - process as chat content
        const content = data.content.content;
        setState((prev) => {
          const lastMessage = prev.chatMessages[prev.chatMessages.length - 1];

          const shouldStartNewMessage =
            prev._hadUserMessageSinceModelStart ||
            !lastMessage ||
            lastMessage.source !== "system";

          if (shouldStartNewMessage) {
            return {
              ...prev,
              chatMessages: [
                ...prev.chatMessages,
                { content, source: "system" },
              ],
              isActive: true,
              _isNewChatMessage: false,
              _hadUserMessageSinceModelStart: false,
            };
          }

          // Concatenate with line break for consecutive model messages
          return {
            ...prev,
            chatMessages: [
              ...prev.chatMessages.slice(0, -1),
              {
                ...lastMessage,
                content: lastMessage.content + "<br />" + content,
              },
            ],
            isActive: true,
            _isNewChatMessage: false,
          };
        });
        return;
      }
      // Ignore function_call, function_response types - they're handled elsewhere
      if (data.type === "function_call" || data.type === "function_response") {
        return;
      }
      console.warn("No event type found in message", data);
      return;
    }

    switch (data.eventType) {
      case "user_message_started":
        setState((prev) => {
          const userInput = data.metadata?.userInput || "";
          const messageSource = data.metadata?.messageSource as MessageSource;

          if (!determineMessageDisplay(messageSource)) {
            return {
              ...prev,
              thinkingMessage: "",
              isActive: true,
              _hadUserMessageSinceModelStart: true,
            };
          }

          const cleanText = userInput
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, " ")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&")
            .trim();

          if (!cleanText) {
            return {
              ...prev,
              thinkingMessage: "",
              functionCalls: [],
              isActive: true,
              _hadUserMessageSinceModelStart: true,
            };
          }
          return {
            ...prev,
            chatMessages: [
              ...prev.chatMessages,
              { content: cleanText, source: "user", contentHtml: userInput },
            ],
            thinkingMessage: "",
            isActive: true,
            _hadUserMessageSinceModelStart: true,
          };
        });
        break;

      case "chat_started":
        // chat_started signals the beginning of a new model response
        // Don't append to previous message - this starts a new one
        setState((prev) => ({
          ...prev,
          isActive: true,
          _isNewChatMessage: true,
          ...(data.metadata?.clientOutputVisible !== undefined && {
            clientOutputVisible: data.metadata?.clientOutputVisible,
          }),
        }));
        break;

      case "chat_streaming":
        setState((prev) => {
          const lastMessage = prev.chatMessages[prev.chatMessages.length - 1];
          const pendingOutputCallIds = prev._pendingOutputCallIds || [];

          if (!data.content) {
            return {
              ...prev,
              isActive: true,
              _isNewChatMessage: false,
            };
          }

          // Start a new message if:
          // - There's been user activity since the last model message started
          // - There's no last message
          // - Last message is not from system (e.g., from user)
          const shouldStartNewMessage =
            prev._hadUserMessageSinceModelStart ||
            !lastMessage ||
            lastMessage.source !== "system";

          if (shouldStartNewMessage) {
            return {
              ...prev,
              chatMessages: [
                ...prev.chatMessages,
                {
                  content: data.content,
                  source: "system",
                  outputCallIds: pendingOutputCallIds,
                },
              ],
              isActive: true,
              _isNewChatMessage: false,
              _hadUserMessageSinceModelStart: false,
              _pendingOutputCallIds: [],
            };
          }

          // Append to existing system message
          // Add line break if this is a new logical message (from chat_started)
          const separator = prev._isNewChatMessage ? "<br />" : "";
          return {
            ...prev,
            chatMessages: [
              ...prev.chatMessages.slice(0, -1),
              {
                ...lastMessage,
                content: lastMessage.content + separator + data.content,
                outputCallIds: Array.from(
                  new Set([
                    ...(lastMessage.outputCallIds || []),
                    ...pendingOutputCallIds,
                  ]),
                ),
              },
            ],
            isActive: true,
            _isNewChatMessage: false,
            _pendingOutputCallIds: [],
          };
        });
        break;

      case "thread_completed":
        setState((prev) => {
          const pendingOutputCallIds = prev._pendingOutputCallIds || [];
          if (pendingOutputCallIds.length === 0) {
            return {
              ...prev,
              isActive: false,
              hasCompleted: true,
            };
          }

          const chatMessages = [...prev.chatMessages];
          let lastModelMessageIndex = -1;
          for (let index = chatMessages.length - 1; index >= 0; index--) {
            if (
              chatMessages[index].source === "system" ||
              chatMessages[index].source === "agent"
            ) {
              lastModelMessageIndex = index;
              break;
            }
          }

          if (lastModelMessageIndex >= 0) {
            const lastModelMessage = chatMessages[lastModelMessageIndex];
            chatMessages[lastModelMessageIndex] = {
              ...lastModelMessage,
              outputCallIds: Array.from(
                new Set([
                  ...(lastModelMessage.outputCallIds || []),
                  ...pendingOutputCallIds,
                ]),
              ),
            };
          } else {
            chatMessages.push({
              content: "",
              source: "system",
              outputCallIds: pendingOutputCallIds,
            });
          }

          return {
            ...prev,
            chatMessages,
            isActive: false,
            hasCompleted: true,
            _pendingOutputCallIds: [],
          };
        });
        break;

      case "thread_failed":
        setState((prev) => ({
          ...prev,
          isActive: false,
          hasFailed: true,
        }));
        break;

      case "thinking_streaming":
        setState((prev) => ({
          ...prev,
          thinkingMessage: data.content,
          isActive: true,
        }));
        break;

      case "function_call": {
        const functionCallData = JSON.parse(data.content);

        if (
          functionCallData.name === "update_plan" ||
          functionCallData.name === "TodoRead" ||
          functionCallData.name === "TodoWrite"
        ) {
          if (
            functionCallData.name === "TodoWrite" &&
            functionCallData.args?.todos
          ) {
            setState((prev) => {
              const todos = functionCallData.args.todos.map(
                (todo: any, index: number) => ({
                  id: todo.id || `todo-${index}`,
                  content: todo.content,
                  status: todo.status,
                  priority: todo.priority,
                }),
              );

              const inProgressTodo = todos.find(
                (t: TodoItem) => t.status === "in_progress",
              );

              return {
                ...prev,
                todos: todos,
                isActive: true,
                currentPlanId: inProgressTodo?.id || prev.currentPlanId,
              };
            });
          }
          if (
            functionCallData.name === "update_plan" &&
            Array.isArray(functionCallData.args?.plan)
          ) {
            setState((prev) => {
              const details = functionCallData.args.details || [];
              const excludedDetails =
                functionCallData.args.excludedDetails || [];

              const detailsWithExclusions = details.map((detail: any) => ({
                ...detail,
                isExcluded: false,
              }));

              excludedDetails.forEach((excluded: any) => {
                detailsWithExclusions.push({
                  title: excluded.title,
                  value: "",
                  isExcluded: true,
                });
              });

              const todos = functionCallData.args.plan.map(
                (todo: any, index: number) => ({
                  id: todo.id || `plan-${index}`,
                  content: todo?.step,
                  step: todo?.step,
                  status: todo?.status,
                  details: detailsWithExclusions,
                }),
              );

              const inProgressTodo = todos.find(
                (t: TodoItem) => t.status === "in_progress",
              );

              return {
                ...prev,
                todos: todos,
                isActive: true,
                currentPlanId: inProgressTodo?.id || prev.currentPlanId,
              };
            });
          }
          return;
        }

        if (
          functionCallData.name === "BookmarkRead" ||
          functionCallData.name === "BookmarkWrite"
        ) {
          if (
            functionCallData.name === "BookmarkWrite" &&
            functionCallData.args?.bookmarks
          ) {
            const bookmarks = functionCallData.args.bookmarks.map(
              (bookmark: any) => ({
                id: bookmark.id,
                type: bookmark.type,
                title: bookmark.title,
                reason: bookmark.reason,
              }),
            );
            setState((prev) => ({
              ...prev,
              bookmarks: [...prev.bookmarks, ...bookmarks],
              isActive: true,
            }));
          }
          return;
        }

        if (
          functionCallData.name === "user_input" ||
          functionCallData.name === "user_question"
        ) {
          const question: UserQuestion = {
            id: functionCallData.id,
            question: functionCallData.args?.question || "User input requested",
            timestamp: Date.now(),
            answered: false,
            question_type: functionCallData.args?.question_type,
            options: functionCallData.args?.options,
          };
          setState((prev) => ({
            ...prev,
            userQuestions: [...prev.userQuestions, question],
            isActive: true,
          }));
          return;
        }

        if (functionCallData.name === "add_calendar_events") {
          const addedEventsMap = new Map<number, AddedEventInfo>();
          if (functionCallData.addedEvents) {
            functionCallData.addedEvents.forEach(
              (addedEvent: {
                index: number;
                eventRef: string;
                eventStatus: string;
                eventModified: boolean;
              }) => {
                addedEventsMap.set(addedEvent.index, {
                  eventRef: addedEvent.eventRef,
                  eventStatus: addedEvent.eventStatus,
                  eventModified: addedEvent.eventModified,
                });
              },
            );
          }

          const calendarEventGroup: CalendarEventGroup = {
            id: functionCallData.id,
            calendar_name:
              functionCallData.args?.calendar_name || "Calendar Events",
            events: functionCallData.args?.events || [],
            timestamp: Date.now(),
            addedEventIndices: functionCallData.addedEventIndices
              ? new Set(functionCallData.addedEventIndices)
              : addedEventsMap.size > 0
                ? new Set(addedEventsMap.keys())
                : undefined,
            addedEvents: addedEventsMap.size > 0 ? addedEventsMap : undefined,
          };
          setState((prev) => ({
            ...prev,
            calendarEvents: [...prev.calendarEvents, calendarEventGroup],
            isActive: true,
          }));
          return;
        }

        if (functionCallData.name === "add_tasks") {
          return;
        }

        if (functionCallData.name === "add_documents") {
          return;
        }
        const delegationData =
          parseDelegationOpportunityFromFunctionCall(functionCallData);
        if (delegationData) {
          const delegation: DelegationOpportunity = delegationData;

          // Find or create a group for this delegation
          setState((prev) => {
            // Check if an opportunity with this functionId already exists (e.g., from backend merge)
            const existingOpp = prev.delegationOpportunities.some((group) =>
              group.opportunities.some(
                (opp) => opp.functionId === functionCallData.id,
              ),
            );

            if (existingOpp) {
              // Already exists, don't create duplicate
              return {
                ...prev,
                isActive: true,
              };
            }

            const existingGroupIndex = prev.delegationOpportunities.findIndex(
              (group) => group.id === "default-group",
            );

            if (existingGroupIndex >= 0) {
              // Add to existing group
              const updatedGroups = [...prev.delegationOpportunities];
              updatedGroups[existingGroupIndex] = {
                ...updatedGroups[existingGroupIndex],
                opportunities: [
                  ...updatedGroups[existingGroupIndex].opportunities,
                  delegation,
                ],
              };
              return {
                ...prev,
                delegationOpportunities: updatedGroups,
                isActive: true,
              };
            } else {
              // Create new group
              const newGroup: DelegationOpportunityGroup = {
                id: "default-group",
                opportunities: [delegation],
                timestamp: Date.now(),
              };
              return {
                ...prev,
                delegationOpportunities: [
                  ...prev.delegationOpportunities,
                  newGroup,
                ],
                isActive: true,
              };
            }
          });
          return;
        }

        setState((prev) => {
          if (prev.functionCalls.some((fc) => fc.id === functionCallData.id)) {
            return { ...prev, isActive: true };
          }
          const functionCall: FunctionCall = {
            id: functionCallData.id,
            name: functionCallData.name,
            displayName: functionCallData.displayName,
            args: functionCallData.args || {},
            state: "queued",
            startTime: Date.now(),
            planId: prev.currentPlanId,
          };
          return {
            ...prev,
            functionCalls: [...prev.functionCalls, functionCall],
            isActive: true,
          };
        });
        break;
      }

      case "function_call_started": {
        const functionCallData = JSON.parse(data.content);

        if (
          functionCallData.name === "TodoRead" ||
          functionCallData.name === "TodoWrite" ||
          functionCallData.name === "BookmarkRead" ||
          functionCallData.name === "BookmarkWrite" ||
          functionCallData.name === "AlertTool" ||
          functionCallData.name === "user_input" ||
          functionCallData.name === "user_question" ||
          functionCallData.name === "add_calendar_events" ||
          functionCallData.name === "add_tasks" ||
          functionCallData.name === "add_documents" ||
          isDelegationFunctionCallName(functionCallData.name)
        ) {
          return;
        }

        const functionCallId = functionCallData.id;
        setState((prev) => ({
          ...prev,
          functionCalls: prev.functionCalls.map((fc) =>
            fc.id === functionCallId
              ? { ...fc, state: "running", startTime: Date.now() }
              : fc,
          ),
          isActive: true,
        }));
        break;
      }

      case "function_call_completed": {
        const completedData = JSON.parse(data.content);
        const functionCallData = completedData.functionCall;

        if (
          functionCallData.name === "user_input" ||
          functionCallData.name === "user_question"
        ) {
          const questionId = functionCallData.id;
          // Extract answer from the tool result
          const answerMatch =
            completedData.toolResult?.match(/User answered: "([^"]*)"/) ||
            completedData.toolResult?.match(/User selected: (.+)$/);
          const answer = answerMatch ? answerMatch[1] : null;

          setState((prev) => ({
            ...prev,
            userQuestions: prev.userQuestions.map((q) =>
              q.id === questionId
                ? { ...q, answered: true, answer: answer || undefined }
                : q,
            ),
            isActive: false,
          }));
          return;
        }

        if (
          functionCallData.name === "TodoRead" ||
          functionCallData.name === "TodoWrite" ||
          functionCallData.name === "BookmarkRead" ||
          functionCallData.name === "BookmarkWrite" ||
          functionCallData.name === "add_calendar_events" ||
          functionCallData.name === "add_tasks" ||
          functionCallData.name === "add_documents" ||
          isDelegationFunctionCallName(functionCallData.name)
        ) {
          return;
        }

        if (functionCallData.name === "AlertTool") {
          const detectedAlerts = createAlertsFromDetection(
            functionCallData?.args?.code,
            accountType,
          );
          const alertsToAdd =
            detectedAlerts.length > 0
              ? detectedAlerts
              : [
                  createGenericAlert(
                    functionCallData?.args?.title || "Alert",
                    functionCallData?.args?.description || "",
                    (functionCallData?.args?.severity as
                      | "info"
                      | "warning"
                      | "error"
                      | "success") || "info",
                    `alert_${functionCallData.id}`,
                  ),
                ];
          setState((prev) => {
            const existingIds = new Set(prev.alerts.map((a) => a.id));
            const newAlerts = alertsToAdd.filter((a) => !existingIds.has(a.id));
            if (newAlerts.length === 0) return prev;
            return {
              ...prev,
              alerts: [...prev.alerts, ...newAlerts],
              isActive: true,
            };
          });
          return;
        }

        const functionCallId = functionCallData.id;
        const hasOutput =
          (completedData.attachments?.length || 0) > 0 ||
          /\/documents\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
            completedData.toolResult || "",
          );
        setState((prev) => ({
          ...prev,
          functionCalls: prev.functionCalls.map((fc) =>
            fc.id === functionCallId
              ? {
                  ...fc,
                  state: "completed",
                  result: completedData.toolResult,
                  executionTimeSec: completedData.executionTimeSec,
                  sources: completedData.sources,
                  attachments: completedData.attachments,
                  endTime: Date.now(),
                }
              : fc,
          ),
          _pendingOutputCallIds: hasOutput
            ? Array.from(
                new Set([
                  ...(prev._pendingOutputCallIds || []),
                  functionCallId,
                ]),
              )
            : prev._pendingOutputCallIds,
          isActive: true,
        }));
        break;
      }
      case "chat_completed":
        setState((prev) => ({
          ...prev,
          isActive: false,
        }));
        break;

      default:
        console.warn("Unknown event type:", data.eventType);
    }
  }, []);

  // Backup refresh mechanism when websocket connection fails
  useEffect(() => {
    if (!onRefresh) {
      return;
    }

    const checkAndRefresh = async () => {
      const now = Date.now();
      const timeSinceLastWebsocket = now - lastWebsocketEventTimeRef.current;

      if (state.isActive && timeSinceLastWebsocket > websocketTimeoutMs) {
        await onRefresh();
      }
    };

    refreshIntervalRef.current = setInterval(
      checkAndRefresh,
      apiRefreshIntervalMs,
    );

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [state.isActive]);

  const isCurrentlyActive =
    state.isActive &&
    state.lastEventTime > 0 &&
    Date.now() - state.lastEventTime < 5000;
  const generalFunctionCalls = state.functionCalls.filter(
    (fc) =>
      ![
        "TodoRead",
        "TodoWrite",
        "BookmarkRead",
        "BookmarkWrite",
        "AlertTool",
        "user_input",
        "user_question",
        "add_calendar_events",
        "add_tasks",
        "add_documents",
      ].includes(fc.name) && !isDelegationFunctionCallName(fc.name),
  );

  const visibleAlerts = state.alerts.filter((alert) => !alert.dismissed);
  const planWithActions = state.todos.map((plan) => ({
    plan,
    actions: generalFunctionCalls.filter((fc) => fc.planId === plan.id),
  }));

  const associatedActionIds = new Set(
    planWithActions.flatMap((p) => p.actions.map((a) => a.id)),
  );
  const unassociatedActions = generalFunctionCalls.filter(
    (fc) => !associatedActionIds.has(fc.id),
  );

  return {
    state,
    actions: {
      handleWebSocketMessage,
      resetState,
      updateState,
      dismissAlert,
      mergeDelegationTasks,
    },
    computed: {
      isCurrentlyActive,
      generalFunctionCalls,
      visibleAlerts,
      planWithActions,
      unassociatedActions,
      hasContent: !!(
        state.chatMessages.length > 0 ||
        state.thinkingMessage ||
        state.todos.length > 0 ||
        state.bookmarks.length > 0 ||
        state.userQuestions.length > 0 ||
        state.calendarEvents.length > 0 ||
        state.addedTasks.length > 0 ||
        state.addedDocuments.length > 0 ||
        state.delegationOpportunities.length > 0 ||
        generalFunctionCalls.length > 0 ||
        visibleAlerts.length > 0 ||
        state.hasFailed
      ),
    },
  };
};
